import React, { useEffect, useRef, useState } from "react";

/**
 * Horizontal LiB / feed card slot. Public and dashboard day-window carousels
 * share this so videos use a durable poster until near the scroll viewport
 * (then mount HoverVideoPlayer). Avoids blank media shells from mounting too
 * many overlay players at once.
 */
export function CarouselMediaSlot({
  scrollRootRef,
  observeViewport,
  className = "link-url",
  children,
}: {
  scrollRootRef: React.MutableRefObject<any>;
  observeViewport: boolean;
  className?: string;
  children: (enableInteractiveMedia: boolean) => React.ReactNode;
}) {
  const slotRef = useRef<HTMLDivElement>(null);
  const [isNearViewport, setIsNearViewport] = useState(!observeViewport);

  useEffect(() => {
    if (!observeViewport) {
      setIsNearViewport(true);
      return;
    }

    const element = slotRef.current;
    if (!element || typeof IntersectionObserver === "undefined") {
      setIsNearViewport(true);
      return;
    }

    let observer: IntersectionObserver | null = null;
    let rafId = 0;

    const connect = () => {
      const root = scrollRootRef?.current ?? null;
      observer = new IntersectionObserver(
        ([entry]) => {
          setIsNearViewport(Boolean(entry?.isIntersecting));
        },
        {
          root,
          rootMargin: "0px 120px 0px 120px",
          threshold: 0,
        },
      );
      observer.observe(element);
    };

    if (scrollRootRef && !scrollRootRef.current) {
      rafId = window.requestAnimationFrame(connect);
    } else {
      connect();
    }

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      observer?.disconnect();
    };
  }, [observeViewport, scrollRootRef]);

  return (
    <div ref={slotRef} className={className}>
      {children(!observeViewport || isNearViewport)}
    </div>
  );
}

export default CarouselMediaSlot;
